import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../../lib/supabase'

export default function MembershipStatus({ supporter, isPaid }) {
  const [validUntil, setValidUntil] = useState(null)
  const [seasonName, setSeasonName] = useState(null)
  const [daysRemaining, setDaysRemaining] = useState(null)
  const [subscriptionStatus, setSubscriptionStatus] = useState('checking')

  useEffect(() => {
    const fetchData = async () => {
      if (!supporter?.id) return

      const [subResult, settingsResult] = await Promise.all([
        supabase.from('subscriptions').select('valid_until')
          .eq('supporter_id', supporter.id).order('valid_until', { ascending: false }).limit(1).single(),
        supabase.from('settings').select('key, value').in('key', ['season_name', 'season_end_date'])
      ])

      if (settingsResult.data) {
        const mapped = {}
        settingsResult.data.forEach(s => mapped[s.key] = s.value)
        if (mapped.season_name) setSeasonName(mapped.season_name)
        if (mapped.season_end_date) {
          const days = Math.max(0, Math.ceil(
            (new Date(mapped.season_end_date) - new Date()) / (1000 * 60 * 60 * 24)
          ))
          setDaysRemaining(days)
        }
      }

      const sub = subResult.data
      if (!sub) {
        setSubscriptionStatus('unpaid')
      } else if (new Date(sub.valid_until) >= new Date()) {
        setSubscriptionStatus('paid')
        setValidUntil(new Date(sub.valid_until).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }))
      } else {
        setSubscriptionStatus('expired')
        setValidUntil(new Date(sub.valid_until).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }))
      }
    }
    fetchData()
  }, [supporter])

  const getSubscriptionDisplay = () => {
    if (subscriptionStatus === 'checking') return { text: '...', color: 'text-gray-400' }
    if (subscriptionStatus === 'paid') return { text: `Active · ${validUntil}`, color: 'text-green-500 dark:text-green-400' }
    if (subscriptionStatus === 'expired') return { text: 'Season Ended', color: 'text-yellow-500 dark:text-yellow-400' }
    return { text: 'Not Subscribed', color: 'text-red-500 dark:text-red-400' }
  }

  const subscription = getSubscriptionDisplay()

  const stats = [
    { label: 'Membership ID', value: supporter?.membership_id || 'N/A', color: 'text-gray-900 dark:text-white' },
    { label: seasonName || 'Subscription', value: subscription.text, color: subscription.color },
    { label: 'Chapter', value: supporter?.chapters?.name || 'N/A', color: 'text-gray-900 dark:text-white' },
    {
      label: 'Season Ends',
      value: daysRemaining !== null
        ? daysRemaining === 0 ? 'Today'
        : daysRemaining === 1 ? '1 day left'
        : `${daysRemaining} days left`
        : 'N/A',
      color: daysRemaining !== null
        ? daysRemaining <= 7 ? 'text-red-500 dark:text-red-400'
        : daysRemaining <= 30 ? 'text-yellow-500 dark:text-yellow-400'
        : 'text-green-500 dark:text-green-400'
        : 'text-gray-400'
    },
  ]

  return (
    <div className="flex flex-col gap-4">

      {/* Unpaid Banner */}
      {subscriptionStatus === 'unpaid' && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800/50 px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="text-yellow-600 dark:text-yellow-400 font-bold text-sm uppercase tracking-widest mb-1">
              Membership Not Active
            </div>
            <p className="text-yellow-700/80 dark:text-yellow-200/70 text-xs leading-relaxed">
              Pay your subscription to unlock your membership card, donate to welfare campaigns, and get your official supporter status.
            </p>
          </div>
          <Link
            to="/subscribe"
            className="bg-yellow-500 hover:bg-yellow-600 text-white px-5 py-2.5 font-bold uppercase tracking-widest text-xs transition-colors whitespace-nowrap shrink-0 text-center"
          >
            Activate Now
          </Link>
        </div>
      )}

      {/* Expired Banner */}
      {subscriptionStatus === 'expired' && (
        <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800/50 px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="text-orange-600 dark:text-orange-400 font-bold text-sm uppercase tracking-widest mb-1">
              Subscription Expired
            </div>
            <p className="text-orange-700/80 dark:text-orange-200/70 text-xs leading-relaxed">
              Your subscription expired on {validUntil}. Renew now to unlock your card and membership benefits.
            </p>
          </div>
          <Link
            to="/subscribe"
            className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 font-bold uppercase tracking-widest text-xs transition-colors whitespace-nowrap shrink-0 text-center"
          >
            Renew Now
          </Link>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <div key={stat.label} className="bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 px-6 py-5 min-w-0">
            <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-2 truncate">
              {stat.label}
            </div>
            <div
              className={`font-black uppercase tracking-tight leading-tight ${stat.color} ${
                String(stat.value).length > 14 ? 'text-sm' : 'text-xl'
              }`}
              title={stat.value}
            >
              {stat.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}